import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiClock, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";
import Labels from "../../services/apiLabels";
import { officeLabel } from "../InspectionUsers/inspectionUserHelpers";
import {
  authHeaders,
  errorText,
  isApiSuccess,
  listFromPayload,
  officeId,
  readJson,
} from "./officeHelpers";
import OfficeHoursDialog from "./OfficeHoursDialog";
import {
  DEFAULT_END,
  DEFAULT_START,
  WEEKDAYS,
  findOfficeInListing,
  listedTimings,
  missingWeekdays,
  timesAreValid,
  timingPayload,
} from "./officeHoursHelpers";

const LIST_PATH = "/dealers/dashbord/offices";
const EMPTY_FORM = {
  id: null,
  day_index: "",
  start: DEFAULT_START,
  end: DEFAULT_END,
};

function OfficeHours() {
  const history = useHistory();
  const location = useLocation();
  const { officeId: officeIdParam } = useParams();
  const auth = getAuth();

  const [office, setOffice] = useState(location.state?.office || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [banner, setBanner] = useState({ type: "", text: "" });
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);

  const timings = useMemo(() => listedTimings(office), [office]);
  const missing = useMemo(() => missingWeekdays(office), [office]);
  const dayOptions = useMemo(() => {
    if (dialog === "edit" && form.day_index !== "") {
      const current = WEEKDAYS.find(
        (day) => String(day.day_index) === String(form.day_index)
      );
      return current
        ? [current, ...missing.filter((day) => day.day_index !== current.day_index)]
        : missing;
    }
    return missing;
  }, [dialog, form.day_index, missing]);

  const goBack = () => history.push(LIST_PATH);

  const loadOffice = useCallback(async () => {
    if (!officeIdParam || !auth?.token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINT(Labels.office_listing), {
        headers: authHeaders(auth.token),
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) {
        throw new Error("load failed");
      }
      const match =
        findOfficeInListing(json, officeIdParam) ||
        listFromPayload(json).find(
          (item) => String(officeId(item)) === String(officeIdParam)
        );
      if (!match) {
        setBanner({
          type: "error",
          text: "This office could not be found.",
        });
        setOffice(null);
        return;
      }
      setOffice(match);
    } catch {
      setBanner({
        type: "error",
        text: "Could not load opening hours. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [auth?.token, officeIdParam]);

  useEffect(() => {
    if (!auth?.token) {
      setLoading(false);
      setBanner({ type: "error", text: "Please sign in again." });
      return;
    }
    loadOffice();
  }, [auth?.token, loadOffice]);

  const closeDialog = () => {
    if (saving) return;
    setDialog(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setFieldErrors({});
  };

  const openAdd = () => {
    setBanner({ type: "", text: "" });
    setFormError("");
    setFieldErrors({});
    setForm({
      ...EMPTY_FORM,
      day_index: missing[0] ? String(missing[0].day_index) : "",
    });
    setDialog("add");
  };

  const openEdit = (row) => {
    setBanner({ type: "", text: "" });
    setFormError("");
    setFieldErrors({});
    setForm({
      id: row.id,
      day_index: String(row.day_index),
      start: row.start || DEFAULT_START,
      end: row.end || DEFAULT_END,
    });
    setDialog("edit");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const next = {};
    if (form.day_index === "") next.day_index = "Choose a weekday.";
    if (!timesAreValid(form.start, form.end)) {
      next.end = "Closing time must be after opening time.";
    }
    setFieldErrors(next);
    if (Object.keys(next).length) return;
    if (!auth?.token) {
      setFormError("Please sign in again.");
      return;
    }

    const isEdit = dialog === "edit" && form.id;
    setSaving(true);
    setFormError("");
    try {
      const path = isEdit
        ? `${Labels.update_office_timing}${form.id}/`
        : Labels.create_office_timming;
      const response = await fetch(API_ENDPOINT(path), {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(auth.token, true),
        body: JSON.stringify(
          timingPayload(officeIdParam, {
            day_index: Number(form.day_index),
            start: form.start,
            end: form.end,
          })
        ),
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) {
        throw new Error(
          errorText(
            json,
            isEdit ? "Could not update these hours." : "Could not add these hours."
          )
        );
      }
      setDialog(null);
      setForm(EMPTY_FORM);
      setBanner({
        type: "success",
        text: isEdit ? "Hours updated." : "Hours added.",
      });
      await loadOffice();
    } catch (err) {
      setFormError(
        err?.message || "Could not save these hours. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id || !auth?.token) return;
    setDeleting(true);
    try {
      const response = await fetch(
        API_ENDPOINT(`${Labels.delete_office_timing}${deleteTarget.id}/`),
        { method: "DELETE", headers: authHeaders(auth.token) }
      );
      const json = await readJson(response);
      if (!isApiSuccess(response, json) && response.status !== 204) {
        throw new Error(errorText(json, "Could not delete these hours."));
      }
      setDeleteTarget(null);
      setBanner({ type: "success", text: "Hours deleted." });
      await loadOffice();
    } catch (err) {
      setBanner({
        type: "error",
        text: err?.message || "Could not delete these hours. Please try again.",
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dd-section dd-offices">
      <div className="dd-arb-intro">
        <span className="dd-arb-intro__badge" aria-hidden>
          <FiClock size={18} />
        </span>
        <div>
          <h2>Opening hours</h2>
          <p>
            Hours already set for {officeLabel(office)}. Add a weekday that is
            still missing.
          </p>
        </div>
      </div>

      {banner.text ? (
        <p
          className={
            banner.type === "success"
              ? "dd-hours-alert dd-hours-alert--success"
              : "dd-hours-alert dd-hours-alert--error"
          }
          role="status"
        >
          {banner.text}
        </p>
      ) : null}

      <div className="dd-toolbar">
        <label className="dd-toolbar__field">
          <span>Offices</span>
          <button type="button" className="dd-btn dd-btn--ghost" onClick={goBack}>
            <FiArrowLeft size={16} aria-hidden />
            Back
          </button>
        </label>
        {missing.length ? (
          <label className="dd-toolbar__field">
            <span>Hours</span>
            <button
              type="button"
              className="dd-btn dd-btn--primary"
              onClick={openAdd}
              disabled={loading}
            >
              <FiPlus size={16} aria-hidden />
              Add hours
            </button>
          </label>
        ) : null}
      </div>

      {loading ? (
        <p className="dd-muted">Loading hours…</p>
      ) : !timings.length ? (
        <div className="dd-empty">
          <strong>No hours yet</strong>
          <p>Add a weekday to start taking appointments at this office.</p>
        </div>
      ) : (
        <div className="dd-hours">
          {timings.map((row) => (
            <div key={row.id || row.day_index} className="dd-hours__row">
              <div className="dd-hours__day">
                <span>{row.day}</span>
              </div>
              <div className="dd-hours__value">Opens {row.start || "—"}</div>
              <div className="dd-hours__value">Closes {row.end || "—"}</div>
              <div className="dd-hours__row-actions">
                <button
                  type="button"
                  className="dd-hours__icon-btn"
                  aria-label={`Edit ${row.day} hours`}
                  onClick={() => openEdit(row)}
                  disabled={!row.id}
                >
                  <FiEdit2 size={15} aria-hidden />
                </button>
                <button
                  type="button"
                  className="dd-hours__icon-btn dd-hours__icon-btn--danger"
                  aria-label={`Delete ${row.day} hours`}
                  onClick={() => setDeleteTarget(row)}
                  disabled={!row.id}
                >
                  <FiTrash2 size={15} aria-hidden />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <OfficeHoursDialog
        open={Boolean(dialog)}
        title={dialog === "edit" ? "Edit hours" : "Add hours"}
        saving={saving}
        saveLabel={dialog === "edit" ? "Save" : "Add"}
        errorText={formError}
        onCancel={closeDialog}
        onSubmit={handleSave}
      >
        <label className={`dd-hours__time${fieldErrors.day_index ? " is-invalid" : ""}`}>
          <span>Weekday</span>
          <select
            value={form.day_index}
            disabled={dialog === "edit"}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, day_index: e.target.value }))
            }
          >
            {dayOptions.map((day) => (
              <option key={day.day_index} value={day.day_index}>
                {day.day}
              </option>
            ))}
          </select>
          {fieldErrors.day_index ? (
            <p className="dd-password__error">{fieldErrors.day_index}</p>
          ) : null}
        </label>
        <label className={`dd-hours__time${fieldErrors.end ? " is-invalid" : ""}`}>
          <span>Opens</span>
          <input
            type="time"
            value={form.start}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, start: e.target.value }))
            }
          />
        </label>
        <label className={`dd-hours__time${fieldErrors.end ? " is-invalid" : ""}`}>
          <span>Closes</span>
          <input
            type="time"
            value={form.end}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, end: e.target.value }))
            }
          />
          {fieldErrors.end ? (
            <p className="dd-password__error">{fieldErrors.end}</p>
          ) : null}
        </label>
      </OfficeHoursDialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete these hours?"
        message={
          deleteTarget
            ? `Remove ${deleteTarget.day} (${deleteTarget.start}–${deleteTarget.end})? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete hours"
        confirming={deleting}
        onCancel={() => (!deleting ? setDeleteTarget(null) : null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default OfficeHours;
